import { useState } from "react";
import { Tag } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { NumInput } from "@/components/NumInput";
import { Button } from "@/components/ui/button";

export const canEditPrice = (role, product, locked) =>
  role === "admin_input" && !locked && (product.selling_price == null || !(product.purchased_qty > 0));

export function PriceEditor({ product, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(product.selling_price != null ? String(product.selling_price) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/products/${product.id}/selling-price`, { selling_price: Number(value) });
      toast.success("Selling price saved");
      if (data.orders_using_product > 0) {
        toast.info(`${data.orders_using_product} existing order(s) keep their current price. Use Edit Order to change them.`);
      }
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button variant="outline" data-testid="sheet-edit-price-btn" className="h-12" onClick={() => setOpen(true)}>
        <Tag className="h-4 w-4" /> {product.selling_price == null ? "Add Price" : "Edit Price"}
      </Button>
    );
  }

  return (
    <div className="w-full space-y-2 rounded-md border bg-accent/40 p-3" data-testid="price-editor">
      <label className="text-xs font-semibold text-muted-foreground" htmlFor="price-editor-input">Selling price (IDR)</label>
      <NumInput id="price-editor-input" data-testid="price-editor-input" className="h-11" placeholder="0" value={value} onValue={setValue} />
      <p className="text-xs text-muted-foreground">Existing customer orders and invoices are not changed.</p>
      <div className="flex gap-2">
        <Button variant="outline" data-testid="price-editor-cancel" className="h-11 flex-1" onClick={() => setOpen(false)}>Cancel</Button>
        <Button data-testid="price-editor-save" className="h-11 flex-1" disabled={saving || value === "" || Number(value) < 0} onClick={save}>
          {saving ? "Saving…" : "Save Price"}
        </Button>
      </div>
    </div>
  );
}
